import { codeFont, numFont } from '../../utils/fonts';
import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
  Typography,
  TextField,
  Alert,
  IconButton,
  CircularProgress,
  Divider,
  Chip,
  Link,
  MenuItem,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import { Link as RouterLink } from 'react-router-dom';
import CloseIcon from '@mui/icons-material/Close';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import LaunchIcon from '@mui/icons-material/Launch';
import EmailOutlinedIcon from '@mui/icons-material/EmailOutlined';
import ReceiptLongIcon from '@mui/icons-material/ReceiptLong';
import axios from 'axios';
import { formatPrice, calcPriceInclVat } from '../../utils/vat';
import { formatQuantity } from '../../utils';
import { useUserStore } from '../../store';

function BatchOrderConfirmDialog({
  open,
  onClose,
  integration,
  selectedPartIds,
  selectedMaintenances,
  selectedParts,
  // The second order source: to_order lines on lähetteet (delivery notes).
  selectedSaleLineIds = [],
  selectedPartsSales = [],
  selectedSaleLines = [],
  onOrderPlaced,
  onStalePartsError,
  inventoryEnabled,
}) {
  const { t } = useTranslation();
  const theme = useTheme();
  const { user, currentOrganization } = useUserStore();
  // Same reason as the pending parts list: the columns do not fit a phone.
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const [comment, setComment] = useState('');
  const [tilaustyyppi, setTilaustyyppi] = useState(1);
  const [toimituskoodi, setToimituskoodi] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [result, setResult] = useState(null);

  const isAtoy = integration?.supplier_type === 'atoy';
  // Sähköpostitilaus: the order goes out as an email, so say where and who gets the copy.
  const isEmail = integration?.supplier_type === 'email';

  const handleClose = () => {
    setComment('');
    setTilaustyyppi(1);
    setToimituskoodi('');
    setError(null);
    setResult(null);
    onClose();
  };

  const total = [...selectedParts, ...selectedSaleLines].reduce((sum, p) => {
    const price = parseFloat(p.purchase_price) || 0;
    const qty = parseFloat(p.quantity) || 0;
    return sum + price * qty;
  }, 0);
  const rowCount = selectedParts.length + selectedSaleLines.length;
  const subtitle = (() => {
    const params = { partCount: rowCount, maintenanceCount: selectedMaintenances.length, saleCount: selectedPartsSales.length };
    if (selectedMaintenances.length && selectedPartsSales.length) return t('purchaseOrders.batchOrder.confirmSubtitleMixed', params);
    if (selectedPartsSales.length) return t('purchaseOrders.batchOrder.confirmSubtitleSales', params);
    return t('purchaseOrders.batchOrder.confirmSubtitle', params);
  })();

  const handleSubmit = async () => {
    setLoading(true);
    setError(null);
    try {
      const payload = {
        integration_id: integration.integration_id,
        maintenance_part_ids: selectedPartIds,
        parts_sale_line_ids: selectedSaleLineIds,
        comment,
      };
      if (isAtoy) {
        payload.tilaustyyppi = tilaustyyppi;
        if (toimituskoodi) payload.toimituskoodi = toimituskoodi;
      }
      const response = await axios.post('/api/supplier-orders/batch-order/', payload);
      setResult(response.data);
      if (onOrderPlaced) onOrderPlaced();
    } catch (err) {
      const statusCode = err.response?.status;
      const data = err.response?.data;

      if (!err.response) {
        setError(t('purchaseOrders.batchOrder.connectionError'));
      } else if (statusCode === 400 && data?.error === 'some_parts_already_ordered') {
        setError(t('purchaseOrders.batchOrder.partsAlreadyOrdered'));
        // Auto-refresh after a short delay
        setTimeout(() => {
          if (onStalePartsError) onStalePartsError();
        }, 2000);
      } else if (statusCode === 502) {
        setError(data?.error || t('purchaseOrders.batchOrder.connectionError'));
      } else if (data?.error) {
        setError(data.error);
      } else {
        setError(t('purchaseOrders.batchOrder.orderFailed'));
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth="sm"
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: { xs: '12px', sm: '16px' },
          m: { xs: 2, sm: 4 },
          maxHeight: { xs: 'calc(100% - 32px)', sm: 'calc(100% - 64px)' },
        },
      }}
    >
      {result ? (
        <>
          <DialogContent sx={{ textAlign: 'center', py: 4 }}>
            <CheckCircleIcon sx={{ fontSize: 56, color: 'success.main', mb: 2 }} />
            <Typography variant="h6" sx={{ fontWeight: 600, mb: 1 }}>
              {result.email_delivery?.to
                ? t('purchaseOrders.emailOrderSuccess', { email: result.email_delivery.to })
                : t('purchaseOrders.batchOrder.success')}
            </Typography>
            {result.purchase_order_number && (
              <Chip
                label={result.purchase_order_number}
                size="small"
                sx={{ ...numFont, fontWeight: 600 }}
              />
            )}
          </DialogContent>
          <DialogActions sx={{ px: 3, pb: 2 }}>
            <Button
              onClick={handleClose}
              variant="contained"
              sx={{ borderRadius: '8px', textTransform: 'none' }}
            >
              {t('Close')}
            </Button>
          </DialogActions>
        </>
      ) : (
        <>
          <DialogTitle
            sx={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              pr: 1,
            }}
          >
            <Typography sx={{ fontWeight: 600, fontSize: '16px' }}>
              {t('purchaseOrders.batchOrder.confirmTitle')}
            </Typography>
            <IconButton onClick={handleClose} size="small">
              <CloseIcon />
            </IconButton>
          </DialogTitle>
          <DialogContent>
            <Typography sx={{ color: 'text.secondary', mb: 2 }}>
              {subtitle}
            </Typography>

            {/* Parts grouped by work order */}
            {selectedMaintenances.map((m) => {
              const maintenanceParts = selectedParts.filter((p) =>
                m.parts?.some((mp) => mp.maintenance_part_id === p.maintenance_part_id)
              );

              return (
                <Box key={m.maintenance_id} sx={{ mb: 2 }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5, flexWrap: 'wrap' }}>
                    <Link
                      component={RouterLink}
                      to={`/maintenances/${m.maintenance_id}`}
                      onClick={(e) => e.stopPropagation()}
                      sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.5, fontSize: '13px', fontWeight: 600 }}
                    >
                      #{m.work_number}
                      <LaunchIcon sx={{ fontSize: 13 }} />
                    </Link>
                    {m.vehicle_registration && (
                      <Typography sx={{ fontSize: '13px', color: 'text.secondary' }}>
                        {m.vehicle_registration}
                      </Typography>
                    )}
                    {m.vehicle_info && (
                      <Typography sx={{ fontSize: '12px', color: 'text.disabled' }}>
                        {m.vehicle_info}
                      </Typography>
                    )}
                  </Box>
                  {maintenanceParts.map((part) =>
                    isMobile ? (
                      <Box key={part.maintenance_part_id} sx={{ pl: 1.5, py: 0.4 }}>
                        <Typography sx={{ fontSize: '13px', lineHeight: 1.3 }}>
                          {part.part_name}
                        </Typography>
                        <Box sx={{ display: 'flex', flexWrap: 'wrap', columnGap: 1, rowGap: 0.25 }}>
                          {part.article_id && (
                            <Typography sx={{ fontSize: '12px', color: 'text.secondary', ...codeFont }}>
                              {part.article_id}
                            </Typography>
                          )}
                          <Typography sx={{ fontSize: '12px', color: 'text.secondary' }}>
                            {formatQuantity(part.quantity)} {'\u00d7'}{' '}
                            {part.purchase_price ? `${formatPrice(part.purchase_price)} \u20ac` : '-'}
                          </Typography>
                          {inventoryEnabled && part.stock_info && (
                            <Typography
                              sx={{
                                fontSize: '12px',
                                color: part.stock_info.is_low_stock ? 'warning.main' : 'text.secondary',
                                fontWeight: part.stock_info.is_low_stock ? 600 : 400,
                              }}
                            >
                              {t('purchaseOrders.pendingParts.stock')}: {formatQuantity(part.stock_info.quantity_in_stock)}
                            </Typography>
                          )}
                        </Box>
                      </Box>
                    ) : (
                      <Box
                        key={part.maintenance_part_id}
                        sx={{ display: 'flex', alignItems: 'center', gap: 1, pl: 1.5, py: 0.25, flexWrap: 'wrap' }}
                      >
                        <Typography sx={{ flex: 1, fontSize: '13px', minWidth: 100 }}>
                          {part.part_name}
                        </Typography>
                        <Typography sx={{ fontSize: '12px', color: 'text.secondary' }}>
                          {part.article_id || ''}
                        </Typography>
                        <Typography sx={{ fontSize: '13px', minWidth: 30, textAlign: 'right' }}>
                          {formatQuantity(part.quantity)}
                        </Typography>
                        {inventoryEnabled && (
                          <Typography
                            sx={{
                              fontSize: '12px',
                              minWidth: 50,
                              textAlign: 'right',
                              color: part.stock_info?.is_low_stock ? 'warning.main' : 'text.secondary',
                              fontWeight: part.stock_info?.is_low_stock ? 600 : 400,
                            }}
                          >
                            {part.stock_info ? formatQuantity(part.stock_info.quantity_in_stock) : '-'}
                          </Typography>
                        )}
                        <Typography sx={{ fontSize: '13px', minWidth: 70, textAlign: 'right' }}>
                          {part.purchase_price ? `${formatPrice(part.purchase_price)} \u20ac` : '-'}
                        </Typography>
                      </Box>
                    )
                  )}
                </Box>
              );
            })}

            {/* Lähete lines grouped by delivery note */}
            {selectedPartsSales.map((sale) => {
              const saleLines = selectedSaleLines.filter((l) =>
                sale.lines?.some((sl) => sl.parts_sale_line_id === l.parts_sale_line_id)
              );
              return (
                <Box key={`s-${sale.parts_sale_id}`} sx={{ mb: 2 }} data-testid={`batch-parts-sale-${sale.parts_sale_id}`}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5, flexWrap: 'wrap' }}>
                    <Link
                      component={RouterLink}
                      to={`/lahetteet/${sale.parts_sale_id}`}
                      onClick={(e) => e.stopPropagation()}
                      sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.5, fontSize: '13px', fontWeight: 600 }}
                    >
                      <ReceiptLongIcon sx={{ fontSize: 14 }} />
                      {t('purchaseOrders.pendingParts.deliveryNote', { number: sale.number })}
                      <LaunchIcon sx={{ fontSize: 13 }} />
                    </Link>
                    {sale.customer_name && (
                      <Typography sx={{ fontSize: '13px', color: 'text.secondary' }}>
                        {sale.customer_name}
                      </Typography>
                    )}
                  </Box>
                  {saleLines.map((line) => (
                    <Box
                      key={line.parts_sale_line_id}
                      sx={{ display: 'flex', alignItems: 'center', gap: 1, pl: 1.5, py: 0.25, flexWrap: 'wrap' }}
                    >
                      <Typography sx={{ flex: 1, fontSize: '13px', minWidth: 100 }}>
                        {line.part_name}
                      </Typography>
                      <Typography sx={{ fontSize: '12px', color: 'text.secondary', ...codeFont }}>
                        {line.article_id || ''}
                      </Typography>
                      <Typography sx={{ fontSize: '13px', minWidth: 30, textAlign: 'right' }}>
                        {formatQuantity(line.quantity)}
                      </Typography>
                      <Typography sx={{ fontSize: '13px', minWidth: 70, textAlign: 'right' }}>
                        {line.purchase_price ? `${formatPrice(line.purchase_price)} \u20ac` : '-'}
                      </Typography>
                    </Box>
                  ))}
                </Box>
              );
            })}

            <Divider sx={{ mb: 1.5 }} />

            {/* Totals */}
            <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}>
              <Typography sx={{ fontSize: '14px', fontWeight: 600 }}>
                {t('purchaseOrders.totalExclVat')}
              </Typography>
              <Typography sx={{ fontSize: '14px', fontWeight: 600 }}>
                {formatPrice(total)} {'\u20ac'}
              </Typography>
            </Box>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 2 }}>
              <Typography sx={{ fontSize: '13px', color: 'text.secondary' }}>
                {t('Total incl. VAT')}
              </Typography>
              <Typography sx={{ fontSize: '13px', color: 'text.secondary' }}>
                {formatPrice(calcPriceInclVat(total))} {'\u20ac'}
              </Typography>
            </Box>

            {isAtoy && (
              <Box sx={{ display: 'flex', gap: 1.5, mb: 1.5 }}>
                <TextField
                  select
                  size="small"
                  label={t('purchaseOrders.atoyTilaustyyppi')}
                  value={tilaustyyppi}
                  onChange={(e) => setTilaustyyppi(parseInt(e.target.value, 10))}
                  sx={{ flex: 1, '& .MuiOutlinedInput-root': { borderRadius: '10px' } }}
                >
                  <MenuItem value={1}>1 — AL Päivätilaus</MenuItem>
                  <MenuItem value={2}>2 — FV Viikkotilaus</MenuItem>
                  <MenuItem value={3}>3 — FK Kausitilaus</MenuItem>
                  <MenuItem value={4}>4 — OS Osaset</MenuItem>
                  <MenuItem value={8}>8 — MO Motonet</MenuItem>
                  <MenuItem value={9}>9 — FV Jälkitoimitus sallittu</MenuItem>
                </TextField>
                <TextField
                  size="small"
                  label={t('purchaseOrders.atoyToimituskoodi')}
                  value={toimituskoodi}
                  onChange={(e) => setToimituskoodi(e.target.value)}
                  placeholder={t('purchaseOrders.atoyToimituskoodiPlaceholder')}
                  helperText={t('purchaseOrders.atoyToimituskoodiHelp')}
                  sx={{ flex: 1, '& .MuiOutlinedInput-root': { borderRadius: '10px' } }}
                />
              </Box>
            )}

            {isEmail && (
              <Alert severity="info" icon={<EmailOutlinedIcon fontSize="inherit" />} sx={{ mb: 1.5 }} data-testid="email-order-info">
                <Typography sx={{ fontSize: '13px' }}>
                  {t('purchaseOrders.emailRecipient', { email: integration.order_email })}
                </Typography>
                {user?.email && (
                  <Typography sx={{ fontSize: '13px' }}>
                    {t('purchaseOrders.emailConfirmation', { email: user.email })}
                  </Typography>
                )}
                {currentOrganization?.lahete_inbox_address && (
                  <Typography sx={{ fontSize: '12px', color: 'text.secondary', mt: 0.5 }}>
                    {t('purchaseOrders.emailLahete', { address: currentOrganization.lahete_inbox_address })}
                  </Typography>
                )}
              </Alert>
            )}

            <TextField
              fullWidth
              multiline
              minRows={2}
              maxRows={4}
              label={isEmail ? t('purchaseOrders.messageToSupplier') : t('purchaseOrders.batchOrder.comment')}
              placeholder={isEmail ? t('purchaseOrders.messagePlaceholder') : t('purchaseOrders.batchOrder.commentPlaceholder')}
              value={comment}
              onChange={(e) => setComment(e.target.value)}
              sx={{ '& .MuiOutlinedInput-root': { borderRadius: '10px' } }}
            />

            {error && (
              <Alert severity="error" sx={{ mt: 2 }}>
                {error}
              </Alert>
            )}
          </DialogContent>
          <DialogActions sx={{ px: 3, pb: 2 }}>
            <Button
              onClick={handleClose}
              sx={{ borderRadius: '8px', textTransform: 'none' }}
            >
              {t('Cancel')}
            </Button>
            <Button
              variant="contained"
              onClick={handleSubmit}
              disabled={loading}
              sx={{
                borderRadius: '8px',
                textTransform: 'none',
                fontWeight: 600,
              }}
            >
              {loading ? (
                <>
                  {t('purchaseOrders.batchOrder.submitting')}{' '}
                  <CircularProgress size={16} sx={{ ml: 1 }} />
                </>
              ) : isEmail ? (
                t('purchaseOrders.submitEmail')
              ) : (
                t('purchaseOrders.batchOrder.submit')
              )}
            </Button>
          </DialogActions>
        </>
      )}
    </Dialog>
  );
}

export default BatchOrderConfirmDialog;
